import { EngagementCategoryEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-category.enum';
import { EngagementToolEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-tool.enum';
import { NavigationModeEnum } from '@empathybroker/tsclient-stats/dist/enums/navigation-mode.enum';

import { ColorEnum, DomainEnum, FeatureIdEnum } from '../enums';

import { Dictionary, FeatureNode, FeaturesTree, TaggingPayload } from '../models';

export class TaggingUtils {

  public static getTaggingPayload(featureId: FeatureIdEnum, domain?: DomainEnum): TaggingPayload {
    const context = !!domain ? TaggingUtils.getTaggingContext(domain) : NavigationModeEnum.GLOBAL;
    const category = TaggingUtils.getTaggingCategory(featureId);
    if (!category) {
      return;
    }
    const tool = TaggingUtils.getTaggingId(featureId, domain) as EngagementToolEnum;
    if (!tool) {
      return;
    }
    return {
      context,
      category,
      tool
    };
  }

  public static getTaggingContext(domain: DomainEnum): NavigationModeEnum {
    return {
      [DomainEnum.SEARCH]: NavigationModeEnum.SEARCH,
      [DomainEnum.BROWSE]: NavigationModeEnum.NAVIGATION,
      [DomainEnum.GLOBAL]: NavigationModeEnum.GLOBAL
    }[domain];
  }

  public static getTaggingCategory(featureId: FeatureIdEnum) {
    return {
      [FeatureIdEnum.OVERALL]: EngagementCategoryEnum.ANALYTICS,
      [FeatureIdEnum.QUERIES]: EngagementCategoryEnum.ANALYTICS,
      [FeatureIdEnum.NOW]: EngagementCategoryEnum.INSIGHTS,
      [FeatureIdEnum.ORIGINS]: EngagementCategoryEnum.INSIGHTS,
      [FeatureIdEnum.KEYWORDS]: EngagementCategoryEnum.INSIGHTS,
      [FeatureIdEnum.EQUALIZE]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.SYNONYM]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.BLACKLIST]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.PROMOTION]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.REDIRECTION]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.CONFIGURATION]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.RELATED_TAGS]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.EXPLAIN]: EngagementCategoryEnum.EXPLAIN,
      [FeatureIdEnum.AUDIT]: EngagementCategoryEnum.AUDIT,
      [FeatureIdEnum.YEARLY_SUMMARY]: EngagementCategoryEnum.MODAL
    }[featureId];
  }

  public static getTaggingId(featureId: FeatureIdEnum, domain: DomainEnum): EngagementCategoryEnum | EngagementToolEnum {
    return {
      [FeatureIdEnum.ANALYTICS]: EngagementCategoryEnum.ANALYTICS,
      [FeatureIdEnum.INSIGHTS]: EngagementCategoryEnum.INSIGHTS,
      [FeatureIdEnum.PLAY]: EngagementCategoryEnum.PLAY,
      [FeatureIdEnum.OVERALL]: EngagementToolEnum.OVERALL,
      [FeatureIdEnum.QUERIES]: EngagementToolEnum.QUERIES,
      [FeatureIdEnum.NOW]: EngagementToolEnum.NOW,
      [FeatureIdEnum.ORIGINS]: EngagementToolEnum.ORIGINS,
      [FeatureIdEnum.KEYWORDS]: EngagementToolEnum.KEYWORDS,
      [FeatureIdEnum.EQUALIZE]: EngagementToolEnum.EQUALIZE,
      [FeatureIdEnum.SYNONYM]: EngagementToolEnum.SYNONYMS,
      [FeatureIdEnum.BLACKLIST]: EngagementToolEnum.BLACKLIST,
      [FeatureIdEnum.PROMOTION]: EngagementToolEnum.PROMOTIONS,
      [FeatureIdEnum.REDIRECTION]: EngagementToolEnum.REDIRECTIONS,
      [FeatureIdEnum.CONFIGURATION]: domain === DomainEnum.SEARCH ? EngagementToolEnum.SEARCH_CONFIGURATIONS
        : EngagementToolEnum.NAVIGATION_CONFIGURATIONS,
      [FeatureIdEnum.RELATED_TAGS]: EngagementToolEnum.RELATED_TAGS,
      [FeatureIdEnum.EXPLAIN]: EngagementToolEnum.EXPLAIN,
      [FeatureIdEnum.AUDIT]: EngagementToolEnum.AUDIT,
      [FeatureIdEnum.YEARLY_SUMMARY]: EngagementToolEnum.YEARLY_SUMMARY
    }[featureId];
  }

  public static getEngagementLabel(id: EngagementCategoryEnum | EngagementToolEnum, messages: Dictionary<string>): string {
    const nameOptions = {
      [EngagementToolEnum.SYNONYMS]: messages[FeatureIdEnum.SYNONYM],
      [EngagementToolEnum.PROMOTIONS]: messages[FeatureIdEnum.PROMOTION],
      [EngagementToolEnum.REDIRECTIONS]: messages[FeatureIdEnum.REDIRECTION],
      [EngagementToolEnum.SEARCH_CONFIGURATIONS]: messages[FeatureIdEnum.CONFIGURATION][DomainEnum.SEARCH],
      [EngagementToolEnum.RELATED_TAGS]: messages[FeatureIdEnum.RELATED_TAGS],
      [EngagementToolEnum.NAVIGATION_CONFIGURATIONS]: messages[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE],
      default: () => messages[id]
    };
    return (nameOptions[id] || nameOptions.default());
  }

  public static getEngagementColor(id: EngagementCategoryEnum | EngagementToolEnum): ColorEnum {
    const colorOptions = {
      [EngagementCategoryEnum.ANALYTICS]: ColorEnum.LIGHT_BLUE,
      [EngagementToolEnum.OVERALL]: ColorEnum.GREEN,
      [EngagementToolEnum.QUERIES]: ColorEnum.YELLOW,
      [EngagementToolEnum.CATEGORIES]: ColorEnum.BLUE,
      [EngagementCategoryEnum.INSIGHTS]: ColorEnum.BLUE,
      [EngagementToolEnum.NOW]: ColorEnum.GREEN,
      [EngagementToolEnum.ORIGINS]: ColorEnum.LIGHT_BLUE,
      [EngagementToolEnum.KEYWORDS]: ColorEnum.YELLOW,
      [EngagementCategoryEnum.PLAY]: ColorEnum.GREEN,
      [EngagementToolEnum.EQUALIZE]: ColorEnum.BLUE,
      [EngagementToolEnum.SYNONYMS]: ColorEnum.LIGHT_BLUE,
      [EngagementToolEnum.BLACKLIST]: ColorEnum.BLACK,
      [EngagementToolEnum.PROMOTIONS]: ColorEnum.RED,
      [EngagementToolEnum.REDIRECTIONS]: ColorEnum.PURPLE,
      [EngagementToolEnum.SEARCH_CONFIGURATIONS]: ColorEnum.YELLOW,
      [EngagementToolEnum.RELATED_TAGS]: ColorEnum.ORANGE,
      [EngagementToolEnum.NEXT_QUERIES]: ColorEnum.ORANGE,
      [EngagementToolEnum.NAVIGATION_CONFIGURATIONS]: ColorEnum.GREEN,
      [EngagementToolEnum.EXPLAIN]: ColorEnum.PURPLE,
      [EngagementToolEnum.AUDIT]: ColorEnum.YELLOW,
      default: () => ColorEnum.PRIMARY
    };
    return (colorOptions[id] || colorOptions.default());
  }

  public static removeBrowseAnalyticsFromFeaturesTree(featuresTree: FeaturesTree): FeaturesTree {
    const browseFeatures = (featuresTree.browse || []).filter((featureNode: FeatureNode) => featureNode.id !== FeatureIdEnum.ANALYTICS);
    return {
      ...featuresTree,
      browse: browseFeatures
    };
  }

}
